import getApplicableContract from "@/composables/useBillings";
import { ACTIVE_STATUS, BILLING_TYPES } from "@/constants";
import { isSessionInPast } from "@/util/date";
import { formatter, formatterWithoutCents } from "./formatter";

/**
 * Housing card price copy: compares active rooms to session default-room price ("base").
 * - No room priced strictly above base → "Included"
 * - Cheapest strictly-above-base room sets "from +$X" (min price minus base)
 * - If any active room matches base price or is default room → "Included or from +..." when X exists,
 *   otherwise only "From +..."
 *
 * @param {Object.<string, number>} roomPrices - Keys are room IDs; values are prices in cents.
 * @param {string} defaultRoomId - Session default room id (base lookup key).
 * @param {Room[]} rooms - Rooms (e.g. merged across units); each has `id` and `status`.
 * @param {boolean} [removeCents=false] - Whether to omit cents when formatting currency.
 * @returns {string}
 */
export const priceLabel = (roomPrices, defaultRoomId, rooms, removeCents = false) => {
  const defaultRoomPrice = roomPrices?.[defaultRoomId] ?? 0;
  const unitRooms = rooms?.filter((room) => room?.status === ACTIVE_STATUS) || [];

  const hasInclusiveOption = unitRooms.some((room) => {
    const price = roomPrices?.[room.id] ?? 0;
    return price <= defaultRoomPrice || room.id === defaultRoomId;
  });

  const pricesAboveBase = unitRooms
    .map((room) => roomPrices?.[room.id] ?? 0)
    .filter((price) => price > defaultRoomPrice);

  if (!pricesAboveBase.length) {
    return "Included";
  }

  const upgradeDifferenceInCents = Math.min(...pricesAboveBase) - defaultRoomPrice;
  const formatted = formatPrice(upgradeDifferenceInCents, removeCents);
  return hasInclusiveOption ? `Included or from +${formatted}` : `From +${formatted}`;
};

/**
 * Formats the given price in cents to a string representation in dollars.
 *
 * @param {number} price - The price in cents to be formatted.
 * @param {boolean} removeCents - Flag indicating whether to remove cents from the formatted price.
 * @returns {string} - The formatted price string.
 */
export const formatPrice = (price, removeCents) => {
  if (removeCents) {
    return formatterWithoutCents.format(price / 100);
  }
  return formatter.format(price / 100);
};

/**
 * Hides program price if it already ended or billing type is Pass through
 *
 * @param {object} session - Information about the program session.
 * @param {object} billingRules - Informations about the billing rules.
 * @param {string | null | undefined} [billingContractId] - When set (e.g. from order), Exchange sessions resolve contract from this id only.
 * @returns {boolean} - A boolean indicating if it should hide the price.
 */
export const hidePrice = (session, billingRules, billingContractId) => {
  if (isSessionInPast(session)) {
    return true;
  }
  const contracts = billingRules?.agreements ?? [];
  if (contracts.length < 1) {
    return false;
  }
  const applicableContract = getApplicableContract(contracts, session, {
    billingContractId,
  });
  return Boolean(applicableContract) && applicableContract?.direct_billing_type !== BILLING_TYPES.PASS_THROUGH;
};
